import { useState, type ReactNode } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuthStore } from '@/store/useAuthStore';
import { routes } from '@/lib/routes';
import { authClient } from '@/lib/authClient';
import { useTeamStore } from '@/store/useTeamStore';
import { useChatStore } from '@/store/useChatStore';
import {
  Users,
  Settings,
  Menu,
  X,
  LogOut,
  LayoutDashboard,
  LayoutGrid,
  Ticket,
  UserCog,
  GraduationCap,
  BarChart3,
  Megaphone,
} from 'lucide-react';
import { toast } from 'sonner';

interface Props {
  children: ReactNode;
  /** When set, shown as small label above page-area for mobile */
  pageTitle?: string;
}

interface NavItem {
  to: string;
  label: string;
  icon: typeof Users;
  exact?: boolean;
}

const COACHING_NAV: NavItem[] = [
  { to: routes.coach.dashboard(), label: 'Klienten', icon: Users, exact: true },
  { to: routes.coach.apps(), label: 'Apps', icon: LayoutGrid },
  { to: routes.coach.settings(), label: 'Einstellungen', icon: Settings },
];

const PLATFORM_NAV: NavItem[] = [
  { to: routes.coach.operator.dashboard(), label: 'Übersicht', icon: LayoutDashboard, exact: true },
  { to: routes.coach.operator.vouchers(), label: 'Vouchers', icon: Ticket },
  { to: routes.coach.operator.users(), label: 'Nutzer', icon: UserCog },
  { to: routes.coach.operator.coaches(), label: 'Coaches', icon: GraduationCap },
  { to: routes.coach.operator.analytics(), label: 'Analytics', icon: BarChart3 },
  { to: routes.coach.operator.announcements(), label: 'Announcements', icon: Megaphone },
];

export function AdminShell({ children, pageTitle }: Props) {
  const location = useLocation();
  const navigate = useNavigate();
  const user = useAuthStore((s) => s.user);
  const isOperator = useAuthStore((s) => s.isOperator);
  const setUser = useAuthStore((s) => s.setUser);
  const setIsOperator = useAuthStore((s) => s.setIsOperator);
  const setProfile = useAuthStore((s) => s.setProfile);
  const [mobileOpen, setMobileOpen] = useState(false);

  function isActive(path: string, exact?: boolean): boolean {
    if (exact) return location.pathname === path;
    return location.pathname === path || location.pathname.startsWith(path + '/');
  }

  async function handleSignOut() {
    try {
      await authClient.signOut();
    } catch {
      toast.error('Abmelden fehlgeschlagen.');
      return;
    }
    setUser(null);
    setIsOperator(false);
    setProfile({ lifeContext: '', workContext: '', currentFocus: '' });
    useTeamStore.getState().clearLocal();
    useChatStore.getState().clearLocal();
    navigate(routes.coach.welcome());
  }

  function renderNavItem(item: NavItem) {
    const Icon = item.icon;
    const active = isActive(item.to, item.exact);
    return (
      <Link
        key={item.to}
        to={item.to}
        onClick={() => setMobileOpen(false)}
        className={`flex items-center gap-2 px-3 py-2 rounded-md text-sm transition-colors ${
          active ? 'bg-muted font-medium' : 'text-muted-foreground hover:bg-muted/60'
        }`}
      >
        <Icon size={16} />
        {item.label}
      </Link>
    );
  }

  const sidebarContent = (
    <>
      <div className="px-4 py-5">
        <Link to={routes.coach.dashboard()} className="flex items-center gap-2.5">
          <img src="/logo-wordmark.png" alt="" className="w-9 h-9 object-contain shrink-0" />
          <div className="min-w-0">
            <span className="block text-sm font-semibold tracking-tight">Teamity</span>
            <span className="block text-[11px] text-muted-foreground">Plattform</span>
          </div>
        </Link>
      </div>
      <nav className="flex-1 px-3 space-y-4 overflow-y-auto">
        <div className="space-y-1">
          <p className="px-3 pb-1 text-[10px] font-medium uppercase tracking-wider text-muted-foreground/70">
            Coaching
          </p>
          {COACHING_NAV.map(renderNavItem)}
        </div>
        {isOperator && (
          <div className="space-y-1 border-t border-border pt-4">
            <p className="px-3 pb-1 text-[10px] font-medium uppercase tracking-wider text-muted-foreground/70">
              Plattform
            </p>
            {PLATFORM_NAV.map(renderNavItem)}
          </div>
        )}
      </nav>
      <div className="px-4 py-4 border-t border-border">
        <p className="text-xs font-medium truncate">{user?.name || user?.email || ''}</p>
        <p className="text-[11px] text-muted-foreground truncate">{user?.email || ''}</p>
        <button
          type="button"
          onClick={handleSignOut}
          className="mt-2 flex items-center gap-1.5 text-xs text-muted-foreground hover:text-foreground transition-colors"
        >
          <LogOut size={12} /> Abmelden
        </button>
      </div>
    </>
  );

  return (
    <div className="flex h-dvh overflow-hidden bg-background">
      {/* Desktop sidebar */}
      <aside className="hidden md:flex flex-col w-56 border-r border-border bg-muted/20">
        {sidebarContent}
      </aside>

      {/* Mobile sidebar drawer */}
      {mobileOpen && (
        <div className="md:hidden fixed inset-0 z-50 flex">
          <div
            className="flex-1 bg-black/30"
            onClick={() => setMobileOpen(false)}
            aria-label="Schließen"
          />
          <div className="w-64 bg-background border-l border-border flex flex-col">
            <div className="flex justify-end p-2">
              <button
                type="button"
                onClick={() => setMobileOpen(false)}
                aria-label="Menü schließen"
                className="p-1.5 rounded-md hover:bg-muted"
              >
                <X size={18} />
              </button>
            </div>
            {sidebarContent}
          </div>
        </div>
      )}

      <main className="flex-1 flex flex-col min-w-0">
        {/* Mobile top bar */}
        <div className="md:hidden flex items-center gap-2 px-3 h-12 border-b border-border bg-background">
          <button
            type="button"
            onClick={() => setMobileOpen(true)}
            aria-label="Menü öffnen"
            className="p-1.5 rounded-md hover:bg-muted"
          >
            <Menu size={18} />
          </button>
          {pageTitle && <span className="text-sm font-medium truncate">{pageTitle}</span>}
        </div>

        <div className="flex-1 overflow-auto">{children}</div>
      </main>
    </div>
  );
}
